import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import {
  selectGuaranteesAdministrationRoles,
  selectGuaranteesAdministrationRolesStatus,
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesSubmitApprovalLetterByUsapokError,
  selectGuaranteesSubmitApprovalLetterByUsapokStatus,
} from '~/store/guarantees/selectors.ts';
import {
  fetchGuaranteesAdministrationRoles,
  fetchGuaranteesApplication,
  submitGuaranteesApprovalLetterByUsapok,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  AdministrationRole,
  GuaranteesApplicationRouteId,
  GuaranteesApplicationStatus,
  GuaranteesFetchStatus,
} from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import {
  USAPOK_APPROVAL_GET_STARTED_PATH,
  USAPOK_APPROVAL_TASKS_PATH,
  USAPOK_APPROVAL_TEXT,
  usapokApprovalEditableStatuses,
} from './constants.ts';

export const useUsapokApprovalPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const administrationRoles = useSelector(selectGuaranteesAdministrationRoles);
  const administrationRolesStatus = useSelector(selectGuaranteesAdministrationRolesStatus);
  const submitApprovalLetterByUsapokStatus = useSelector(
    selectGuaranteesSubmitApprovalLetterByUsapokStatus
  );
  const submitApprovalLetterByUsapokError = useSelector(
    selectGuaranteesSubmitApprovalLetterByUsapokError
  );
  const hasUsapokAccess = administrationRoles.includes(AdministrationRole.USAPOK_ADMINISTRATOR);
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRouteApplication?.applicationNumber) ||
    getStringValue(currentRouteApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isEditableStatus = usapokApprovalEditableStatuses.has(normalizedStatus);
  const currentApplicationVersion =
    typeof currentRouteApplication?.version === 'number'
      ? currentRouteApplication.version
      : undefined;
  const redirectPath = !contragentId ? USAPOK_APPROVAL_GET_STARTED_PATH : '';
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const isSubmittingApprovalLetterByUsapok =
    submitApprovalLetterByUsapokStatus === GuaranteesFetchStatus.Loading;
  const isUsapokAccessLoading =
    administrationRolesStatus === GuaranteesFetchStatus.Idle ||
    administrationRolesStatus === GuaranteesFetchStatus.Loading;
  const isUsapokAccessDenied = !isUsapokAccessLoading && !hasUsapokAccess;
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const navigateToTasks = useCallback(() => {
    navigate(USAPOK_APPROVAL_TASKS_PATH, { replace: true });
  }, [navigate]);
  const handleCloseUsapokAccessModal = useCallback(() => {
    handleCloseAlertModal();
    navigateToTasks();
  }, [handleCloseAlertModal, navigateToTasks]);
  const isSubmitDisabled =
    !effectiveApplicationId ||
    currentApplicationVersion === undefined ||
    !hasUsapokAccess ||
    !isEditableStatus ||
    isApplicationLoading ||
    isSubmittingApprovalLetterByUsapok;
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: navigateToTasks,
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigateToTasks, statusConfig]
  );
  const handleSubmitApprovalLetterByUsapok = useCallback(async () => {
    if (!effectiveApplicationId || currentApplicationVersion === undefined) {
      return;
    }

    try {
      await dispatch(
        submitGuaranteesApprovalLetterByUsapok({
          applicationId: effectiveApplicationId,
          version: currentApplicationVersion,
        })
      ).unwrap();
      setModalSuccess({
        text: USAPOK_APPROVAL_TEXT.submitSuccessMessage,
        buttonValue: USAPOK_APPROVAL_TEXT.successCloseButton,
        onClick: navigateToTasks,
        onClose: navigateToTasks,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
    }
  }, [
    currentApplicationVersion,
    dispatch,
    effectiveApplicationId,
    navigateToTasks,
    setModalSuccess,
    showGuaranteesRequestError,
  ]);

  useEffect(() => {
    if (administrationRolesStatus !== GuaranteesFetchStatus.Idle) {
      return;
    }

    void dispatch(fetchGuaranteesAdministrationRoles());
  }, [administrationRolesStatus, dispatch]);

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === GuaranteesApplicationRouteId.New) {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useEffect(() => {
    if (isUsapokAccessDenied) {
      setModalAlert({ text: USAPOK_APPROVAL_TEXT.accessDeniedText });
    }
  }, [isUsapokAccessDenied, setModalAlert]);

  useFreshGuaranteesRequestError(
    applicationError || submitApprovalLetterByUsapokError,
    showGuaranteesRequestError
  );

  return {
    effectiveApplicationId,
    handleCloseAlertModal,
    handleCloseSuccessModal,
    handleCloseUsapokAccessModal,
    handleSubmitApprovalLetterByUsapok,
    headerProps,
    isApplicationLoading,
    isSubmitDisabled,
    isSubmittingApprovalLetterByUsapok,
    isUsapokAccessDenied,
    isUsapokAccessLoading,
    modalAlert,
    modalSuccess,
    redirectPath,
    showGuaranteesRequestError,
  };
};
